import { createPlateEditor, createPlugins, withPlate, createPluginFactory } from '@udecode/plate';
import { Editor, Transforms, createEditor } from 'slate';
import { ReactEditor } from 'slate-react';
import { useContext, useEffect, useRef } from 'react';

import ComposerContext from 'components/composer/contexts/composer_context';
import deserializeHtml from 'components/text_editor_new/lib/deserialize_html';
import { focusFirstPlaceholder } from './placeholders';
import { SnippetContentType } from 'models/answers/snippet';
import { updateVariables } from './variables';
import { useVariables } from 'components/composer/contexts/variables_context';

// Depedencies: VariablesPlugin created using useCreateVariablesPlugin hook
export function createAnswersPlugin() {
  return createPluginFactory({
    key: 'answers',
    withOverrides: withAnswersCommands,
  })();
}

function withAnswersCommands(editor) {
  editor.getSaturatedFragment = html => {
    // We do this by creating an entirely separate editor controller and updating the variables in it first,
    // then just returning the children as if nothing ever happened ;)
    const fragmentEditor = createFragmentEditor(editor);
    const fragment = deserializeHtml(html);
    fragmentEditor.children = fragment;
    Editor.normalize(fragmentEditor, { force: true });
    fragmentEditor.updateVariables();
    return fragmentEditor.children;
  };
  return editor;
}

function createFragmentEditor(editor) {
  const plugins = createPlugins(editor.plugins);
  const fragmentEditor = withPlate(createEditor(), { plugins });
  return fragmentEditor;
}

export function useAnswers(editor, plugins) {
  const { answerToInsert, clearInsertedAnswer } = useContext(ComposerContext);
  const variablesRef = useVariablesRef();

  const prevAnswerRef = useRef(null);

  // We insert answers by setting "answerToInsert" in our composer context.. when the component using this
  // hook "sees" that update, it inserts it into the editor, then clears it from the context.
  useEffect(() => {
    // If the composer was closed initially, we need to wait for it to be initialized first before doing any
    // of this mumbo jumbo.
    if (!editor) {
      return;
    }

    if (answerToInsert !== prevAnswerRef.current) {
      prevAnswerRef.current = answerToInsert;

      if (answerToInsert) {
        const channelType = answerToInsert.channelType;
        const item = answerToInsert.item;
        let content = item.findContentByLanguage(answerToInsert.language);
        const itemHtml = channelType && content.getBodyByType(SnippetContentType[channelType]);
        if (itemHtml) {
          insertAnswer(editor, plugins, variablesRef, itemHtml);
        }
        clearInsertedAnswer();
      }
    }
  }, [editor, clearInsertedAnswer, answerToInsert]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function insertAnswer(editor, plugins, variablesRef, html) {
  const fragment = editor.getSaturatedFragment(html);

  ReactEditor.focus(editor);

  // If we insert an answer and undo when there's nothing else in the editor, the selection can
  // become null. Maybe a Slate bug? Anyways, just focus the first node in the editor and select
  // that in that case.
  if (!editor.selection) {
    const entry = Editor.first(editor, []);
    Transforms.select(editor, Editor.range(editor, entry[1]));
  }

  const previousSelection = editor.selection;
  Transforms.insertFragment(editor, fragment);

  // If we insert an answer with a placeholder in it, focus the first placeholder in the inserted
  // fragment.
  const newSelection = editor.selection;
  const startPoint = Editor.start(editor, previousSelection);
  const endPoint = Editor.end(editor, newSelection);
  focusFirstPlaceholder(editor, { at: { anchor: startPoint, focus: endPoint } });

  return;
}

// We create a new temporary editor to insert the fragment into, which allows us to fully update the variables
// before the actual editor ever sees it. This prevents us from seeing the un-saturated variables for one moment
// after inserting an answer (and also allowing agents to Ctrl-Z to the unupdated state).
export function getFragmentWithSaturatedVariables(plugins, variablesRef, html) {
  // We do this by creating an entirely separate editor controller and updating the variables in it first,
  // then just returning the children as if nothing ever happened ;)
  const fragmentEditor = createPlateEditor({ plugins });
  const fragment = deserializeHtml(html);
  fragmentEditor.children = fragment;
  Editor.normalize(fragmentEditor, { force: true });
  updateVariables(fragmentEditor, variablesRef);
  return fragmentEditor.children;
}

function useVariablesRef() {
  const variables = useVariables();
  const ref = useRef(variables);
  useEffect(() => {
    ref.current = variables;
  }, [variables]);
  return ref;
}
